import { BASE, authFetch, extractError, postForm, request } from "./client";

export interface VoiceBrief {
  key: string;
  name: string;
}

export interface VoiceSummary {
  id: string;
  name: string;
  role: string;
  provider?: "qwen" | "tencent";
  builtin?: boolean;
}

export interface VoiceListResponse {
  voices: VoiceSummary[];
  provider: "qwen" | "tencent";
  can_create: boolean;
}

export interface VoiceDetail {
  id: string;
  name: string;
  role: string;
  avatar: string;
  characteristics: string;
  ref_text: string;
  provider?: "qwen" | "tencent";
  builtin?: boolean;
}

export function listVoices(): Promise<VoiceListResponse> {
  return request("/voices");
}

export function getVoice(id: string): Promise<VoiceDetail> {
  return request(`/voices/${id}`);
}

export function deleteVoice(id: string): Promise<{ id: string }> {
  return request(`/voices/${id}`, { method: "DELETE" });
}

export function voiceAudioUrl(id: string): string {
  return `${BASE}/voices/${id}/audio`;
}

export function voiceEmbeddingUrl(id: string): string {
  return `${BASE}/voices/${id}/embedding`;
}

export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConflictError";
  }
}

export async function transcribeAudio(blob: Blob): Promise<string> {
  const fd = new FormData();
  fd.append("audio", blob, "recording.wav");
  const data = await (await postForm("/voices/transcribe", fd)).json();
  return data.text as string;
}

export async function saveVoice(
  data: { name: string; role: string; avatar: string; ref_text: string },
  audioBlob: Blob,
): Promise<{ id: string }> {
  const fd = new FormData();
  fd.append("name", data.name);
  fd.append("role", data.role);
  fd.append("avatar", data.avatar);
  fd.append("ref_text", data.ref_text);
  fd.append("audio", audioBlob, "ref_audio.wav");
  const res = await authFetch("/voices", { method: "POST", body: fd });
  if (res.status === 409) {
    const body = await res.json();
    throw new ConflictError(body.detail ?? "名称已存在");
  }
  if (!res.ok) throw new Error(await extractError(res));
  return res.json();
}

export async function previewVoice(voiceId: string, text: string): Promise<Blob> {
  const fd = new FormData();
  fd.append("voice_id", voiceId);
  fd.append("text", text);
  return (await postForm("/voices/preview", fd)).blob();
}

export async function previewVoiceUpload(audioBlob: Blob, refText: string, text: string): Promise<Blob> {
  const fd = new FormData();
  fd.append("audio", audioBlob, "ref_audio.wav");
  fd.append("ref_text", refText);
  fd.append("text", text);
  return (await postForm("/voices/preview-upload", fd)).blob();
}
